import { useCallback, useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent } from "react";

/** Minimal History-API router (path-based; Pages serves /index.html for every path). */
const NAV_EVENT = "apphq:navigate";

function subscribe(cb: () => void): () => void {
  window.addEventListener("popstate", cb);
  window.addEventListener(NAV_EVENT, cb);
  return () => {
    window.removeEventListener("popstate", cb);
    window.removeEventListener(NAV_EVENT, cb);
  };
}

const getLocation = () => window.location.pathname + window.location.search;

export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  if (to === getLocation()) return;
  if (opts.replace) window.history.replaceState(null, "", to);
  else window.history.pushState(null, "", to);
  window.dispatchEvent(new Event(NAV_EVENT));
}

/** Current pathname + URLSearchParams, re-rendering on navigation. */
export function useLocation(): { path: string; search: URLSearchParams; setSearch: (patch: Record<string, string | null>) => void } {
  const loc = useSyncExternalStore(subscribe, getLocation, () => "/");
  const q = loc.indexOf("?");
  const path = q === -1 ? loc : loc.slice(0, q);
  const search = new URLSearchParams(q === -1 ? "" : loc.slice(q + 1));
  const setSearch = useCallback((patch: Record<string, string | null>) => {
    const next = new URLSearchParams(window.location.search);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === "") next.delete(k);
      else next.set(k, v);
    }
    const s = next.toString();
    navigate(window.location.pathname + (s ? `?${s}` : ""), { replace: true });
  }, []);
  return { path, search, setSearch };
}

export function Link({ to, onClick, ...rest }: { to: string } & AnchorHTMLAttributes<HTMLAnchorElement>) {
  function handle(e: MouseEvent<HTMLAnchorElement>) {
    onClick?.(e);
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(to);
  }
  return <a href={to} onClick={handle} {...rest} />;
}
